import React, { useEffect, useState } from "react";
import { formatarCnj } from "@/lib/masks";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { API } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, FileText, MessageSquare, Trash2, Plus, Clock, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatDateBR } from "@/lib/dates";
import { DADOS } from "../lib/larguras";

function InfoItem({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[#335480] text-sm font-medium uppercase tracking-wide mb-1">{label}</p>
      <p className="text-[#001533] text-base">{value}</p>
    </div>
  );
}

export default function ProcessoDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [processo, setProcesso] = useState(null);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [notas, setNotas] = useState([]);
  const [intimacoes, setIntimacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [novaNota, setNovaNota] = useState("");
  const [savingNota, setSavingNota] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, mRes, nRes, iRes] = await Promise.all([
        axios.get(`${API}/processos/${id}`, { withCredentials: true }),
        axios.get(`${API}/processos/${id}/movimentacoes`, { withCredentials: true }),
        axios.get(`${API}/processos/${id}/notas`, { withCredentials: true }),
        axios.get(`${API}/processos/${id}/intimacoes`, { withCredentials: true }),
      ]);
      setProcesso(pRes.data);
      setMovimentacoes(Array.isArray(mRes.data) ? mRes.data : []);
      setNotas(Array.isArray(nRes.data) ? nRes.data : []);
      setIntimacoes(Array.isArray(iRes.data) ? iRes.data : []);
    } catch {
      toast.error("Erro ao carregar processo");
      navigate("/processos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const handleSync = async () => {    setSyncing(true);
    try {
      const res = await axios.post(`${API}/processos/${id}/sync`, {}, { withCredentials: true });
      if (res.data.synced_today) {
        toast.info("Processo já foi sincronizado hoje");
      } else {
        toast.success("Processo sincronizado!");
        setProcesso(res.data.processo);
        const mRes = await axios.get(`${API}/processos/${id}/movimentacoes`, { withCredentials: true });
        setMovimentacoes(mRes.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const handleAddNota = async (e) => {
    e.preventDefault();
    if (!novaNota.trim()) return;
    setSavingNota(true);
    try {
      const res = await axios.post(
        `${API}/processos/${id}/notas`,
        { conteudo: novaNota.trim() },
        { withCredentials: true }
      );
      setNotas((prev) => [res.data, ...prev]);
      setNovaNota("");
      toast.success("Nota adicionada");
    } catch {
      toast.error("Erro ao salvar nota");
    } finally {
      setSavingNota(false);
    }
  };

  const handleDeleteNota = async (notaId) => {
    try {
      await axios.delete(`${API}/processos/${id}/notas/${notaId}`, { withCredentials: true });
      setNotas((prev) => prev.filter((n) => n.nota_id !== notaId));
      toast.success("Nota removida");
    } catch {
      toast.error("Erro ao remover nota");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-8 space-y-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-[#F4F7FB] rounded animate-pulse" />)}
        </div>
      </Layout>
    );
  }

  if (!processo) return null;

  return (
    <Layout>
      <div className={DADOS}>
        {/* Back + Header */}
        <div className="mb-8 animate-fade-in-1">
          <button
            onClick={() => navigate("/processos")}
            className="flex items-center gap-2 text-[#335480] hover:text-[#003882] mb-4 text-base font-medium transition-colors"
            data-testid="back-to-processos-btn"
          >
            <ArrowLeft size={18} />
            Voltar a Processos
          </button>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1
                className="text-3xl font-bold text-[#001533] font-mono mb-2"
                style={{ fontFamily: "Cabinet Grotesk" }}
                data-testid="processo-numero"
              >
                {formatarCnj(processo.numero_cnj)}
              </h1>
              {processo.descricao && (
                <p className="text-[#335480] text-lg">{processo.descricao}</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="export-pdf-btn"
                onClick={() => window.open(`${API}/export/processo/${id}/pdf`, "_blank")}
                variant="outline"
                className="border-[#D1DEEE] text-[#335480] hover:text-[#003882] hover:border-[#003882] flex-shrink-0"
              >
                <Download size={16} />
                PDF
              </Button>
              <Button
                data-testid="sync-processo-btn"
                onClick={handleSync}
                disabled={syncing}
                variant="outline"
                className="border-[#D1DEEE] text-[#335480] hover:text-[#003882] hover:border-[#003882] flex-shrink-0"
              >
                <RefreshCw size={16} className={syncing ? "animate-spin" : ""} />
                {syncing ? "Sincronizando..." : "Sincronizar"}
              </Button>
            </div>
          </div>
          {processo.last_sync_at && (
            <p className="text-[#335480] text-sm mt-2 flex items-center gap-1">
              <Clock size={13} />
              Última sincronização: {format(new Date(processo.last_sync_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
            </p>
          )}
        </div>

        {/* Process Info Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 animate-fade-in-2">
          <div className="bg-white border border-[#D1DEEE] rounded-lg p-5">
            <InfoItem label="Classe" value={processo.classe} />
          </div>
          <div className="bg-white border border-[#D1DEEE] rounded-lg p-5">
            <InfoItem label="Assunto" value={processo.assunto} />
          </div>
          <div className="bg-white border border-[#D1DEEE] rounded-lg p-5">
            <InfoItem label="Órgão Julgador" value={processo.orgao_julgador} />
          </div>
          <div className="bg-white border border-[#D1DEEE] rounded-lg p-5">
            <InfoItem label="Valor da Causa" value={processo.valor_causa ? `R$ ${processo.valor_causa}` : null} />
          </div>
          {(processo.instancia || processo.tribunal) && (
            <div className="bg-white border border-[#D1DEEE] rounded-lg p-5">
              <InfoItem label="Instância" value={processo.instancia} />
            </div>
          )}
          {processo.data_distribuicao && (
            <div className="bg-white border border-[#D1DEEE] rounded-lg p-5">
              <InfoItem label="Data de Ajuizamento" value={formatDateBR(processo.data_distribuicao)} />
            </div>
          )}
          {processo.situacao && (
            <div className="bg-white border border-[#D1DEEE] rounded-lg p-5">
              <InfoItem label="Situação" value={processo.situacao} />
            </div>
          )}
          {processo.tribunal && (
            <div className="bg-white border border-[#D1DEEE] rounded-lg p-5">
              <InfoItem label="Tribunal" value={processo.tribunal} />
            </div>
          )}
        </div>

        {/* Partes */}
        {processo.partes && processo.partes.length > 0 && (
          <div className="bg-white border border-[#D1DEEE] rounded-lg p-6 mb-8 animate-fade-in-2">
            <h3 className="text-lg font-bold text-[#001533] mb-4" style={{ fontFamily: "Cabinet Grotesk" }}>
              Partes do Processo
            </h3>
            <div className="grid md:grid-cols-2 gap-3">
              {processo.partes.map((parte, i) => (
                <div key={i} className="p-4 bg-[#F4F7FB] rounded-lg border border-[#D1DEEE]">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                      parte.polo === "ATIVO" ? "bg-[#003882] text-white" : "bg-[#335480] text-white"
                    }`}>
                      {parte.polo || parte.tipoParte || "Parte"}
                    </span>
                    {parte.tipoParte && parte.polo && (
                      <span className="text-xs text-[#335480]">{parte.tipoParte}</span>
                    )}
                  </div>
                  <p className="text-[#001533] font-semibold text-base">{parte.nome || "—"}</p>
                  {parte.representantes?.length > 0 && (
                    <div className="mt-2 pl-3 border-l-2 border-[#D1DEEE]">
                      {parte.representantes.map((r, j) => (
                        <p key={j} className="text-[#335480] text-sm">
                          <span className="font-medium">{r.tipo || "Advogado"}:</span> {r.nome}
                          {r.oab?.length > 0 && ` (OAB/${r.oab[0].uf} ${r.oab[0].numero})`}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="animate-fade-in-3">
          <Tabs defaultValue="movimentacoes">
            <TabsList className="bg-[#F4F7FB] border border-[#D1DEEE] p-1 mb-6">
              <TabsTrigger
                value="movimentacoes"
                data-testid="tab-movimentacoes"
                className="data-[state=active]:bg-white data-[state=active]:text-[#003882] font-medium text-base"
              >
                Movimentações ({movimentacoes.length})
              </TabsTrigger>
              <TabsTrigger
                value="notas"
                data-testid="tab-notas"
                className="data-[state=active]:bg-white data-[state=active]:text-[#003882] font-medium text-base"
              >
                Notas ({notas.length})
              </TabsTrigger>
              <TabsTrigger
                value="intimacoes"
                data-testid="tab-intimacoes"
                className="data-[state=active]:bg-white data-[state=active]:text-[#003882] font-medium text-base"
              >
                Intimações ({intimacoes.length})
              </TabsTrigger>
            </TabsList>

            {/* Movimentações */}
            <TabsContent value="movimentacoes">
              <div className="bg-white border border-[#D1DEEE] rounded-lg overflow-hidden">
                {movimentacoes.length === 0 ? (
                  <div className="p-12 text-center text-[#335480]">
                    <FileText size={40} className="mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Nenhuma movimentação encontrada.</p>
                    <p className="text-sm mt-1">Clique em "Sincronizar" para buscar os andamentos deste processo.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#D1DEEE]">
                    {movimentacoes.map((mov, i) => (
                      <div
                        key={i}
                        data-testid={`movimentacao-${i}`}
                        className="px-6 py-5 hover:bg-[#F4F7FB] transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 text-center min-w-[120px]">
                            <p className="text-[#003882] font-bold text-base">
                              {formatDateBR(mov.data)}
                            </p>
                            <p className="text-[#335480] text-xs">
                              {mov.data?.substring(11, 16) || ""}
                            </p>
                            {mov.tipo && (
                              <span className="text-xs text-[#335480]">{mov.tipo}</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-[#001533] text-base leading-relaxed">{mov.descricao}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Notas */}
            <TabsContent value="notas">
              <div className="bg-white border border-[#D1DEEE] rounded-lg p-6 mb-4">
                <h4 className="text-base font-semibold text-[#001533] mb-3">Nova nota</h4>
                <form onSubmit={handleAddNota} className="space-y-3">
                  <Textarea
                    data-testid="new-nota-input"
                    value={novaNota}
                    onChange={(e) => setNovaNota(e.target.value)}
                    placeholder="Escreva sua anotação..."
                    rows={3}
                    className="text-base border-[#D1DEEE] resize-none"
                  />
                  <Button
                    type="submit"
                    data-testid="save-nota-btn"
                    disabled={savingNota || !novaNota.trim()}
                    className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold"
                  >
                    <Plus size={16} />
                    {savingNota ? "Salvando..." : "Adicionar nota"}
                  </Button>
                </form>
              </div>

              <div className="space-y-3">
                {notas.map((nota, i) => (
                  <div
                    key={nota.nota_id}
                    data-testid={`nota-${i}`}
                    className="bg-white border border-[#D1DEEE] rounded-lg p-5"
                  >
                    <div className="flex justify-between items-start gap-4">
                      <p className="text-[#001533] text-base leading-relaxed flex-1 whitespace-pre-wrap">
                        {nota.conteudo}
                      </p>
                      <button
                        onClick={() => handleDeleteNota(nota.nota_id)}
                        className="p-1.5 rounded text-[#335480] hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                        data-testid={`delete-nota-${i}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                    <p className="text-[#335480] text-xs mt-3">
                      {nota.author_name && `${nota.author_name} • `}
                      {(() => { try { return format(new Date(nota.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }); } catch { return nota.created_at || "—"; } })()}
                    </p>
                  </div>
                ))}
                {notas.length === 0 && (
                  <div className="p-12 text-center text-[#335480] bg-white border border-[#D1DEEE] rounded-lg">
                    <MessageSquare size={40} className="mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Nenhuma nota ainda.</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Intimações vinculadas */}
            <TabsContent value="intimacoes">
              <div className="bg-white border border-[#D1DEEE] rounded-lg overflow-hidden">
                {intimacoes.length === 0 ? (
                  <div className="p-12 text-center text-[#335480]">
                    <FileText size={40} className="mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Nenhuma intimação vinculada.</p>
                    <p className="text-sm mt-1">Intimações deste processo aparecem aqui automaticamente.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#D1DEEE]">
                    {intimacoes.map((int, i) => (
                      <div key={i} className="px-6 py-4 hover:bg-[#F4F7FB] transition-colors">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-[#001533] text-base font-medium">{int.tipo_comunicacao || "Intimação"}</p>
                            <p className="text-[#335480] text-sm mt-0.5">{int.orgao_julgador || int.orgao || "—"}</p>
                          </div>
                          <span className="text-[#335480] text-sm whitespace-nowrap">{formatDateBR(int.data_disponibilizacao)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
}
